import React, { useCallback, useEffect, useRef } from "react"

import { Divider, Flex, Spin, Typography } from "antd"
import cls from "classname"
import { useDispatch, useSelector } from "react-redux"

import { getCompareParams } from "../../Components/Filters/CompareSelect/presets"
import { useDemoBannerVisible } from "../../hooks/useDemoBannerVisible"
import { getDates } from "../../Utils/utils"
import { useDemoDataToggle } from "../User/hooks/useDemoDataToggle"
import Header from "./components/Header/Header"
import MastercardMonitoringSection from "./Mastercard/components/MastercardMonitoringSection/MastercardMonitoringSection"
import HealthActions from "./reducer"
import EnrollmentInfoModal from "./VAMP/components/EnrollmentModal/EnrollmentInfoModal"
import EnrollmentModal from "./VAMP/components/EnrollmentModal/EnrollmentModal"
import EstimatedProgramStatus from "./VAMP/components/EstimatedProgramStatus/EstimatedProgramStatus"
import MainChartBlock from "./VAMP/components/MainChartBlock/MainChartBlock"
import MonitoringProgram from "./VAMP/components/MonitoringProgram/MonitoringProgram"
import { useBusinessAccountId } from "./VAMP/hooks/useBusinessAccountId"
import { useVampHistoricalDataMode } from "./VAMP/hooks/useHistoricalDataMode"

import "./styles.less"

const { Text } = Typography

const HealthMonitoringPage = () => {
  // Manual switch for ECM block view while backend data is not ready yet.
  // true: show placeholder, false: show ECM block preview (VAMP-like layout).
  const isEcmPlaceholderVisible = true

  const dispatch = useDispatch()
  const { isAdmin, userInfo } = useSelector((state) => state.auth)
  const { isMobile } = useSelector((state) => state.nav)
  const { isShowDemoData } = useDemoDataToggle()
  const isDemoBannerVisible = useDemoBannerVisible()
  const { businessAccount } = useSelector((state) => state.user)
  const {
    mainInfo,
    filters,
    monitoringInitialized,
    descriptorsList,
    descriptor,
    monitoringInitializing,
    monitoringInitError
  } = useSelector((state) => state.health)

  const { isHistoricalDataMode } = useVampHistoricalDataMode()

  const business_account_id = useBusinessAccountId()

  const changeFilters = (filters) => {
    dispatch(HealthActions.healthSetProp("filters", filters))
  }

  const handleChangeFilter = useCallback((newFilters) => {
    if (Array.isArray(newFilters.period)) {
      changeFilters({
        ...newFilters,
        period: { type: "custom", period: newFilters.period }
      })
    } else {
      changeFilters(newFilters)
    }
  }, [])

  useEffect(() => {
    if (filters?.period && descriptor) {
      const period_query = getDates(filters.period)
      const compare_query = getCompareParams(filters?.compare, period_query)
      dispatch(
        HealthActions.getHealthMainInfoRequest({
          ...period_query,
          ...compare_query,
          business_account: business_account_id,
          descriptor,
          ...(isHistoricalDataMode ? { historical_data: true } : {})
        })
      )
    }
  }, [business_account_id, filters, isShowDemoData, descriptor, isHistoricalDataMode])

  const previousBAId = useRef(business_account_id)

  useEffect(() => {
    if (!monitoringInitialized || previousBAId.current !== business_account_id) {
      previousBAId.current = business_account_id
      dispatch(HealthActions.monitoringInit({ business_account: business_account_id }))
    }
  }, [business_account_id, monitoringInitialized])

  if (monitoringInitializing)
    return <Spin className={cls("health-monitoring-spin", { admin: isAdmin })} spinning={true} />

  if (!monitoringInitialized)
    return (
      <Flex className={cls("monitoring-page-init-error", { admin: isAdmin })}>
        <Text strong>{monitoringInitError?.message || "Failed to fetch monitoring data"}</Text>
      </Flex>
    )

  return (
    <div
      className={cls("health-page", "health-monitoring-page", {
        "is-admin-view": isAdmin,
        "is-mobile": isMobile,
        "demo-banner-visible": isDemoBannerVisible
      })}
    >
      {monitoringInitialized && (
        <>
          <Header filters={filters} handleChangeFilter={handleChangeFilter} type="monitoring" />
          {isAdmin && <Divider style={{ margin: 0 }} />}
        </>
      )}
      <Flex gap={40} vertical className="page-content">
        {monitoringInitialized ? (
          <>
            <MainChartBlock filters={filters} business_account_id={business_account_id} />
            <MonitoringProgram business_account_id={business_account_id} />
            <EstimatedProgramStatus business_account_id={business_account_id} />
          </>
        ) : (
          <Flex className={cls("monitoring-page-init-error", { admin: isAdmin })}>
            <Text strong>{monitoringInitError?.message || "Failed to fetch monitoring data"}</Text>
          </Flex>
        )}
        <MastercardMonitoringSection filters={filters} business_account_id={business_account_id} />
      </Flex>
      {monitoringInitialized && (
        <>
          <EnrollmentModal />
          <EnrollmentInfoModal />
        </>
      )}
    </div>
  )
}

export default HealthMonitoringPage
